import React, { useState, useEffect, useRef } from "react";
import { Drawer, Checkbox, Typography, Button, message, Input } from "antd";
import { subjobs, getSubJobs } from "@/services/common";
import userInfo from "@/Components/user";
import { validateInput } from "@/Pages/Project/Flow/FixedData/Diagram/validateInput";
import { buildTaskInput } from "@/Pages/Project/Flow/FixedData/func/buildInput";
import { openErrorNotification } from "@/Pages/Project/Flow/FixedData/Diagram/ErrorsNotice";

const { Text } = Typography;

interface FixedCalModalProps {
  visible: boolean;
  dataSource: any; // 流程图数据（GoJS模型数据，包含nodeDataArray）
  onClose: () => void;
  selectTaskData: any; // 当前选中的计算任务
  notificationApi?: any;
  notificationKey?: string;
  setQueryTask?: () => void;
  refreshSubJobs?: () => void;
  multipleNodes?: any[]; // Ctrl+点击在流程图中选中的元件
  selectTreeItem:(treeItem)=>void;
  runGroupTask:(nodeNames:string[],runName:string)=>Promise<number|null>;
}

const FixedCalModal: React.FC<FixedCalModalProps> = ({
  visible,
  dataSource,
  onClose,
  selectTaskData,
  notificationApi,
  notificationKey,
  setQueryTask,
  refreshSubJobs,
  multipleNodes,
  selectTreeItem,
  runGroupTask,
}) => {
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [taskName, setTaskName] = useState("");
  const multipleNodesRef = useRef(multipleNodes);

  // 始终保持 ref 为最新值
  useEffect(() => {
    multipleNodesRef.current = multipleNodes;
  }, [multipleNodes]);

  // 抽屉打开时，根据图中选中的元件预勾选
  useEffect(() => {
    if (visible) {
      const nodes = multipleNodesRef.current;
      if (nodes && nodes.length > 0) {
        setSelectedElements(nodes.map((obj: any) => obj["nodeName"]));
      } else {
        setSelectedElements([]);
      }
      setTaskName("");
    }
  }, [visible]);
  const allItems = (dataSource?.nodeDataArray || []).filter(
    (item: any) => !item.isGroup && item.elementName && item.nodeName?.trim() !== "",
  );
  const allItemNames = allItems.map((item: any) => item.nodeName);

  // 全选相关状态
  const isAllSelected = selectedElements.length === allItemNames.length && allItemNames.length > 0;
  const isIndeterminate =
    selectedElements.length > 0 && selectedElements.length < allItemNames.length;

  // 构建 groupMap：key 是 groupName，groups里按 group key 分组
  const groupDataMap = new Map<string, any[]>();
  const groups = (dataSource?.nodeDataArray || []).filter((item: any) => item.isGroup);
  const groupKeyToName = new Map<number, string>();
  groups.forEach((g: any) => groupKeyToName.set(g.key, g.groupName));

  // 按工艺段分组
  allItems.forEach((item: any) => {
    const groupKey = item.group;
    const groupName = groupKeyToName.get(groupKey) || "无工艺段";
    if (!groupDataMap.has(groupName)) groupDataMap.set(groupName, []);
    groupDataMap.get(groupName)!.push(item);
  });

  useEffect(() => {
    if (visible) {
      if (multipleNodes && multipleNodes.length > 0) {
        setSelectedElements(multipleNodes.map((obj: any) => obj["nodeName"]));
      } else {
        setSelectedElements([]);
      }
      setTaskName("");
    }
  }, [visible]);

  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      setSelectedElements(allItemNames);
    } else {
      setSelectedElements([]);
    }
  };

  // 构建展示文本：全选的工艺段用工艺段名替代元件名
  const selectedDisplayText = (() => {
    const fullySelectedGroupNames: string[] = [];
    const coveredNames = new Set<string>();
    Array.from(groupDataMap.entries()).forEach(([groupName, items]) => {
      const groupItemNames = items.map((item: any) => item.nodeName);
      if (groupItemNames.every((name: string) => selectedElements.includes(name))) {
        fullySelectedGroupNames.push(groupName);
        groupItemNames.forEach((name: string) => coveredNames.add(name));
      }
    });
    const remaining = selectedElements.filter((name) => !coveredNames.has(name));
    return [...fullySelectedGroupNames, ...remaining].join(',');
  })();

  // 切换某个工艺段的全选/取消
  const handleGroupSelectAll = (groupName: string, checked: boolean) => {
    const groupItemNames = (groupDataMap.get(groupName) || []).map((item: any) => item.nodeName);
    if (checked) {
      setSelectedElements((prev) => Array.from(new Set([...prev, ...groupItemNames])));
    } else {
      setSelectedElements((prev) => prev.filter((name) => !groupItemNames.includes(name)));
    }
  };

  // 切换单个元件的选中状态
  const toggleItem = (nodeName: string, checked: boolean) => {
    if (checked) {
      setSelectedElements((prev) => [...prev, nodeName]);
    } else {
      setSelectedElements((prev) => prev.filter((name) => name !== nodeName));
    }
  };

  const handleOk = async () => {
    if (!selectTaskData) {
      message.warning("请选择计算任务");
      return;
    }
    if (taskName.trim() === "") {
      message.warning("请输入计算名称");
      return;
    }
    if (selectedElements.length === 0) {
      message.warning("请至少选择一个单元");
      return;
    }
    
    setLoading(true);
    let nodeNames = selectedElements;
    let runName = taskName.trim();
    const resultId = await runGroupTask(nodeNames, runName);
    setLoading(false);
    if (resultId) {
      await refreshSubJobs?.();
    }
    onClose();
  };

  return (
    <Drawer
      title="单元组合运算"
      placement="right"
      open={visible}
      onClose={onClose}
      width={700}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={loading} onClick={handleOk}>
            确认
          </Button>
        </div>
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          marginBottom: 10,
          paddingBottom: 10,
          borderBottom: "1px solid #EEE",
        }}
      >
        <div style={{ fontSize: 14, color: "#000", marginRight: 20 }}>计算名称：</div>
        <Input
          value={taskName}
          onChange={(e) => {
            setTaskName(e.target.value);
          }}
          style={{ flex: 1 }}
        />
      </div>
      <div
        style={{
          fontSize: 14,
          color: "#1b6ae7",
          fontWeight: "bold",
          marginBottom: 10,
          paddingBottom: 10,
          borderBottom: "1px solid #EEE",
        }}
      >
        {`选择单元组合：${selectedDisplayText}`}
      </div>
      <div style={{ marginBottom: 16 }}>
        <Checkbox
          indeterminate={isIndeterminate}
          checked={isAllSelected}
          onChange={handleSelectAll}
        >
          全选
        </Checkbox>
      </div>
      <div
        style={{ width: "100%", overflowY: "auto", marginBottom: 20 }}
      >
        {Array.from(groupDataMap.entries()).map(([groupName, items]) => {
          const groupItemNames = items.map((item: any) => item.nodeName);
          const groupCheckedCount = groupItemNames.filter((name: string) => selectedElements.includes(name)).length;
          const isGroupAllChecked = groupCheckedCount === groupItemNames.length;
          const isGroupIndeterminate = groupCheckedCount > 0 && groupCheckedCount < groupItemNames.length;
          return (
          <div key={groupName}>
            <div style={{ fontWeight: "bold", marginBottom: 10, color: "#1b6ae7", display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={isGroupAllChecked}
                indeterminate={isGroupIndeterminate}
                onChange={(e) => handleGroupSelectAll(groupName, e.target.checked)}
                style={{ marginRight: 8 }}
              />
              {groupName}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
              {items.map((item) => (
                <div key={item.key} style={{ flex: "0 0 auto", width: 150, marginBottom: 10 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Checkbox
                      checked={selectedElements.includes(item.nodeName)}
                      onChange={(e) => toggleItem(item.nodeName, e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    <Text ellipsis={{ tooltip: item.nodeName }}>{item.nodeName}</Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })}
      </div>
    </Drawer>
  );
};

export default FixedCalModal;
